import React, { useEffect, useId, useRef, useState } from 'react'
import { Control, useWatch, UseFormRegister } from 'react-hook-form'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { T2EFormData } from 'hooks/useTranslationForm'
import useSuggest from 'hooks/useSuggest'
import { SuggestMethod, trackSuggestPick } from 'lib/analytics/events'
import SuggestPanel from './suggest-panel'

type Props = {
   register: UseFormRegister<T2EFormData>
   control: Control<T2EFormData>
   onSubmit: () => void
   onClear: () => void
   // Merged with react-hook-form's own ref so focus/reset helpers and RHF point
   // at the same node (see the homepage box).
   textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>
   placeholder?: string
}

/**
 * The compact inline lookup bar shown inside the dictionary pages' (compact)
 * header — the always-small counterpart to the homepage's morphing box. Kept
 * self-contained so it doesn't drag in the homepage hero's chrome.
 *
 * It answers as it is typed in: see the suggest panel below the field, and
 * hooks/useSuggest for where its rows come from.
 */
const DictionarySearchBar = ({
   register,
   control,
   onSubmit,
   onClear,
   textareaRef,
   placeholder = 'Look up a word in Thai or English…',
}: Props) => {
   const sourceText = useWatch({ control, name: 'sourceText' })
   const hasText = Boolean(sourceText && sourceText.length > 0)
   const { ref: registerRef, ...sourceTextField } = register('sourceText')

   const navigate = useNavigate()
   const pathname = useRouterState({ select: (s) => s.location.pathname })
   const { groups, items, query } = useSuggest(control, 'word_page')

   const [isOpen, setIsOpen] = useState(false)
   // -1 means "nothing picked", and that is the state the panel opens in.
   // Auto-selecting the first row would make ⏎ open it, so typing "hai" and
   // hitting enter would take you to "hair" — the box has to search what was
   // actually typed. A row only becomes the ⏎ target once ↑↓ moves onto it.
   const [keyboardIndex, setKeyboardIndex] = useState(-1)
   // Purely visual. Kept apart from the keyboard cursor so a mouse resting over
   // the panel can't change what ⏎ does either.
   const [hoverIndex, setHoverIndex] = useState(-1)
   const wrapperRef = useRef<HTMLDivElement | null>(null)
   // The word pages seed the box with the entry currently being read
   // (pages/thai-word.tsx), so "there is text in the box" is not the same as
   // "the visitor is searching". Without this the panel would open itself on
   // every word page load, over the entry the visitor just asked for.
   const hasTypedRef = useRef(false)

   const listboxId = useId()
   const optionId = (index: number) => `${listboxId}-option-${index}`

   // Rows are the only thing the panel draws, so no rows means no panel: a
   // phrase (useSuggest never asks about one) and a lookup that matched nothing
   // both leave the box alone rather than dropping something in the way.
   const showPanel = isOpen && groups.length > 0
   const selectableCount = items.length
   const activeIndex = keyboardIndex < selectableCount ? keyboardIndex : -1

   // Typing again drops the cursor: the rows underneath it have changed, so
   // whatever was picked no longer answers what is now in the box.
   useEffect(() => {
      setKeyboardIndex(-1)
      setHoverIndex(-1)
   }, [query, selectableCount])

   // Arriving on a new entry ends the search that got us there.
   useEffect(() => {
      setIsOpen(false)
      hasTypedRef.current = false
   }, [pathname])

   // Clicking anywhere outside the field or the panel dismisses it. Pointerdown
   // rather than click, so the panel is gone before a click elsewhere lands.
   useEffect(() => {
      if (!showPanel) return
      const onPointerDown = (event: PointerEvent) => {
         if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false)
      }
      document.addEventListener('pointerdown', onPointerDown)
      return () => document.removeEventListener('pointerdown', onPointerDown)
   }, [showPanel])

   // Reports the pick and shuts the panel. Navigation is the caller's: a click
   // is the row's own <Link> following itself, ⏎ has to be sent.
   const pick = (index: number, method: SuggestMethod) => {
      const item = items[index]
      if (!item) return
      trackSuggestPick({
         surface: 'word_page',
         method,
         index,
         rowCount: items.length,
         isThaiHeadword: item.isThaiHeadword,
         query,
      })
      setIsOpen(false)
   }

   const openSelected = (): boolean => {
      const item = activeIndex >= 0 ? items[activeIndex] : undefined
      if (!item) return false
      pick(activeIndex, 'enter')
      navigate(item.route)
      return true
   }

   return (
      // Sized here rather than on the field: the panel is positioned against
      // this wrapper (left-0/right-0), so one constraint keeps the box and the
      // suggestions exactly the same width. Uncapped — it fills the header's
      // container.
      <div className="relative w-full" ref={wrapperRef}>
         <form
            className={`relative z-20 w-full bg-searchFieldBg border border-hairline transition-all duration-150 focus-within:border-translit focus-within:bg-searchFieldBgFocus focus-within:ring-3 focus-within:ring-searchRing ${
               // Square off the bottom while the panel is docked underneath, so
               // the two read as one object rather than a pill with a card
               // floating under it.
               showPanel ? 'rounded-[16px_16px_10px_10px]' : 'rounded-full'
            }`}
            onSubmit={(e) => {
               e.preventDefault()
               setIsOpen(false)
               onSubmit()
            }}
         >
            <div className="flex items-center gap-[10px] pr-[8px] pl-[16px]">
               <span
                  className="shrink-0 inline-flex items-center text-textMuted [&_.material-icons]:text-[22px]"
                  aria-hidden="true"
               >
                  <span className="material-icons">search</span>
               </span>
               <textarea
                  ref={(el) => {
                     registerRef(el)
                     if (textareaRef) textareaRef.current = el
                  }}
                  className="block flex-1 min-w-0 w-full border-0 outline-none resize-none overflow-hidden bg-transparent py-[12px] font-latin text-[19px] leading-[1.4] min-h-[24px] text-bodyBlack placeholder:text-textMuted"
                  placeholder={placeholder}
                  aria-label="searchbox"
                  data-gramm="false"
                  rows={1}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showPanel}
                  aria-controls={showPanel ? listboxId : undefined}
                  aria-activedescendant={
                     showPanel && activeIndex >= 0 ? optionId(activeIndex) : undefined
                  }
                  {...sourceTextField}
                  onChange={(e) => {
                     hasTypedRef.current = true
                     setIsOpen(true)
                     void sourceTextField.onChange(e)
                  }}
                  onFocus={() => {
                     if (hasTypedRef.current) setIsOpen(true)
                  }}
                  onKeyDown={(e) => {
                     if (e.nativeEvent.isComposing) return

                     if (e.key === 'Escape') {
                        setIsOpen(false)
                        return
                     }

                     if (
                        showPanel &&
                        selectableCount > 0 &&
                        (e.key === 'ArrowDown' || e.key === 'ArrowUp')
                     ) {
                        e.preventDefault()
                        setHoverIndex(-1)
                        // The walk includes -1, the typed text itself, as a stop
                        // between the last row and the first: arrowing off either
                        // end of the list hands the box back rather than looping
                        // straight past it.
                        const stops = selectableCount + 1
                        const from = activeIndex + 1
                        const next =
                           e.key === 'ArrowDown' ? (from + 1) % stops : (from - 1 + stops) % stops
                        setKeyboardIndex(next - 1)
                        return
                     }

                     if (e.key === 'Enter') {
                        e.preventDefault()
                        // Only a row the visitor actually moved onto wins. With
                        // nothing picked this is an ordinary submit, so the text
                        // in the box is what gets searched — typing "hai" looks
                        // up "hai", it doesn't open "hair".
                        if (showPanel && openSelected()) return
                        setIsOpen(false)
                        onSubmit()
                     }
                  }}
               />
               {hasText && (
                  <button
                     type="button"
                     className="shrink-0 w-[20px] inline-flex items-center justify-center border-0 bg-transparent cursor-pointer text-textMuted transition-colors duration-150 hover:text-bodyBlack [&_.material-icons]:text-[20px]"
                     title="Clear"
                     aria-label="Clear"
                     onClick={() => {
                        setIsOpen(false)
                        hasTypedRef.current = false
                        onClear()
                     }}
                  >
                     <span className="material-icons">close</span>
                  </button>
               )}
               <button
                  type="submit"
                  className="shrink-0 h-[40px] w-[40px] inline-flex items-center justify-center border-none rounded-full bg-transparent cursor-pointer text-translit transition-colors duration-150 hover:bg-lightBlueTransparent [&_.material-icons]:text-[22px]"
                  title="Search"
                  aria-label="Search"
               >
                  <span className="material-icons">arrow_forward</span>
               </button>
            </div>

            {showPanel && (
               <SuggestPanel
                  groups={groups}
                  items={items}
                  selectedIndex={activeIndex}
                  hoveredIndex={hoverIndex}
                  onHover={setHoverIndex}
                  onPick={(index) => pick(index, 'click')}
                  rawQuery={sourceText ?? ''}
                  listboxId={listboxId}
                  optionId={optionId}
               />
            )}
         </form>

         {/* Announced rather than shown: the count is already visible on each
             group header, but a screen reader needs to hear the list change. */}
         <span className="sr-only" role="status" aria-live="polite">
            {showPanel && selectableCount > 0
               ? `${selectableCount} suggestion${selectableCount === 1 ? '' : 's'}`
               : ''}
         </span>
      </div>
   )
}

export default DictionarySearchBar
